// Apple In-App Purchase helper for iOS (WebToNative wrapper).
// Centralises:
//   • Fetching & caching the plan_type → Apple Product ID map (/api/iap/apple/products)
//   • Invoking the native InAppPurchase SDK with proper accountToken
//   • Forwarding the resulting receipt to /api/iap/apple/verify
//
// On non-iOS contexts (web/Android), `isIosNativeApp` is false and the
// callers fall back to the existing Razorpay flow.

import { isIosApp, isAndroidApp } from 'webtonative';
import { inAppPurchase, getReceiptData } from 'webtonative/InAppPurchase';
import { iapAPI } from './api';

// Flag the rest of the app can import directly.
export const isIosNativeApp = !!isIosApp;

// iter140 — True only inside the WebToNative Android shell. Used to flip
// Razorpay Standard Checkout into UPI-Intent mode (`webview_intent: true`)
// so UPI payments emit `upi://` deep links for the native WebView to hand
// off to GPay/PhonePe/Paytm instead of attempting an in-page collect flow.
export const isAndroidNativeApp = !!isAndroidApp;

// Plan → Apple Product ID cache. Populated once per session.
let productMapPromise = null;
export const loadAppleProductMap = () => {
  if (!productMapPromise) {
    productMapPromise = iapAPI.getProducts()
      .then((r) => r.data?.plan_to_product_id || {})
      .catch(() => ({}));
  }
  return productMapPromise;
};

// Generate a short UUID-ish token used as Apple's app_account_token to tie a
// transaction to a server-side user (helps with refund + restore audits).
const newAccountToken = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'gx-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

// Pull the receipt out of the heterogenous SDK response. Different WebToNative
// builds expose it under different keys, so we probe all of them.
const extractReceipt = (response) => {
  if (!response) return '';
  return (
    response.receiptData ||
    response.transactionReceipt ||
    response.receipt ||
    response.appStoreReceipt ||
    (response.data && (response.data.receiptData || response.data.transactionReceipt)) ||
    ''
  );
};

/**
 * Trigger a native Apple IAP purchase and verify it server-side.
 *
 * @param {object} args
 * @param {string} args.productId  Apple Product ID (e.g. "in.genzox.app.7dayplan")
 * @param {string} [args.listingId] Optional - when present and the product is an add-on,
 *                                   the backend applies it directly to that listing.
 * @returns {Promise<object>} Resolves with the verify endpoint's JSON response.
 */
export const purchaseApple = ({ productId, listingId } = {}) => {
  if (!isIosNativeApp) return Promise.reject(new Error('Not an iOS native app'));
  if (!productId) return Promise.reject(new Error('Missing productId'));

  const accountToken = newAccountToken();

  return new Promise((resolve, reject) => {
    // Hard timeout — if the native iOS wrapper never invokes our callback
    // (e.g. the IPA was built without IAP entitlements), the UI would
    // otherwise sit in "Processing…" forever and look broken to App Reviewers.
    // 90s is long enough for a real StoreKit prompt + Face ID + receipt round-trip.
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error("Couldn't reach the App Store. Please make sure you're signed into your Apple ID and try again."));
    }, 90000);

    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      fn(value);
    };

    try {
      inAppPurchase({
        productId,
        accountToken,
        callback: async (response) => {
          // Apple cancellation / failure surfaces with isSuccess=false. Some
          // builds use status="cancelled" so we treat any non-success as fail.
          const ok = response?.isSuccess === true || response?.status === 'success';
          if (!ok) {
            settle(reject, new Error(response?.message || response?.error || 'Purchase cancelled'));
            return;
          }
          let receipt = extractReceipt(response);
          // Fallback: explicitly request the latest receipt from the native side
          // if the purchase callback didn't include one inline.
          if (!receipt) {
            receipt = await new Promise((res) => {
              try {
                getReceiptData({ callback: (r) => res(extractReceipt(r)) });
                // Defensive timeout — don't hang forever
                setTimeout(() => res(''), 5000);
              } catch (_) { res(''); }
            });
          }
          if (!receipt) {
            settle(reject, new Error('No App Store receipt returned by the native layer'));
            return;
          }
          try {
            const verifyResp = await iapAPI.verify({
              receipt_data: receipt,
              product_id: productId,
              account_token: accountToken,
              ...(listingId ? { listing_id: listingId } : {}),
            });
            settle(resolve, verifyResp.data);
          } catch (err) {
            settle(reject, err);
          }
        },
      });
    } catch (err) {
      settle(reject, err);
    }
  });
};

/**
 * Restore previously-purchased Apple IAP entitlements for the signed-in user.
 * Pulls the latest App Store receipt from the device and re-verifies it.
 */
export const restoreApplePurchases = () => {
  if (!isIosNativeApp) return Promise.reject(new Error('Not an iOS native app'));
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error("Couldn't reach the App Store. Please try again."));
    }, 60000);
    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      fn(value);
    };
    try {
      getReceiptData({
        callback: async (response) => {
          const receipt = extractReceipt(response);
          if (!receipt) { settle(reject, new Error('No receipt available on this device')); return; }
          try {
            const resp = await iapAPI.restore({ receipt_data: receipt, product_id: 'restore' });
            settle(resolve, resp.data);
          } catch (err) {
            settle(reject, err);
          }
        },
      });
    } catch (err) { settle(reject, err); }
  });
};
